import React, { useContext } from "react";
import { Box, Grid, IconButton, Paper, Stack } from "@mui/material";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import styled from "styled-components";
import InvestmentMainChart from "src/scenes/overview/overview-components/investment-main-chart";
import DeliveryThroughputChart from "src/scenes/overview/overview-components/delivery-throughput-chart";
import DeliveryLeadTimeChart from "src/scenes/overview/overview-components/delivery-leadtime-chart";
import DeliveryScopeCreepChart from "src/scenes/overview/overview-components/delivery-scope-creep-chart";
import CreditExpenditureMainChart from "src/scenes/overview/overview-components/credit-expenditure-main-chart";
import CreditExpenditureBacklogChart from "src/scenes/overview/overview-components/credit-expediture-backlog-chart";
import CreditExpenditureBugsDetectedChart from "src/scenes/overview/overview-components/credit-expediture-bugs-detected-chart";
import CreditExpenditureReworkChart from "src/scenes/overview/overview-components/credit-expediture-rework-chart";
import CreditExpenditureMaintainabilityChart from "src/scenes/overview/overview-components/credit-expediture-maintainability-chart";
import CreditExpenditureSilosChart from "src/scenes/overview/overview-components/credit-expediture-silos-chart";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import { OverviewContext } from "src/components/providers/overview-provider";
import OverviewRefreshSection from "src/scenes/overview/overview-components/overview-refresh-section";
import DeliveryReviewTimeChart from "src/scenes/overview/overview-components/delivery-review-time-chart";
import OverviewExpandModal from "src/scenes/overview/overview-components/charts-expand-modal/charts-expand-modal";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import InfoIcon from "@mui/icons-material/Info";
import ReactCardFlip from "react-card-flip";
import Tooltip from "@mui/material/Tooltip";
import { docsPathConstants, getDocsPath } from "src/utils/docs-path-constants";
import { useOrg } from "src/components/providers/org-provider";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";
import TemplateTitle from "src/components/template/template-title";
import KnowledgebaseIconLink from "src/components/knowledgebase-icon-link";
import InvalidIntegrationButton from "src/components/invalid-integration-button";
import Template from "src/components/template/template";
import { useActiveDate } from "src/scenes/dashboard/date-provider";
import { startOfDay, subMonths } from "date-fns";
import ExternalLink from "../../components/external-link";

const H = styled.h1`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 0;
`;

const ChartPaper = styled(Paper)`
    min-height: 240px;
    border-radius: 14px;
`;

const HeaderStack = styled(Stack)`
    width: 100%;
    background-color: #5b5b5b;
    padding: 4px 8px 2px 16px;
    border-radius: 14px 14px 0 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const InfoStack = styled(Stack)`
    padding: 18px;
`;

const InfoHeaderText = styled.p`
    margin: 0 0 5px 0;
    font-size: 15px;
    font-weight: 600;
`;

const InfoText = styled.p`
    margin: 0 0 10px 0;
    font-size: 12px;
    font-weight: 200;
`;

const StyledMenuItem = styled(MenuItem)`
    font-size: 13px;
`;
const OverviewAnalytics = () => {
    const [expandedChart, setExpandedChart] = React.useState(undefined);
    const [meta, setMeta] = React.useState(undefined);
    const { org } = useOrg();
    const { dashboard } = useTemplateViewEdit();
    const { handleChange: handleDateChange } = useActiveDate();

    const [flippedCharts, setFlippedCharts] = React.useState({
        investment: false,
        throughput: false,
        leadTime: false,
        scopeCreep: false,
        reviewTime: false,
        creditCost: false,
        backlogSize: false,
        bugsDetected: false,
        rework: false,
        maintainability: false,
        codeSilos: false,
    });

    const {
        executiveTimeseriesApi,
        executiveApi,
        setRefresh,
        aggregationPeriod,
        selectedTeam,
        setAggregationPeriod,
        semesters,
        setSemesters,
    } = useContext(OverviewContext);
    const codeQualityActivated =
        executiveApi &&
        executiveApi.data &&
        executiveApi.data.maintainability &&
        executiveApi.data.maintainability.codeQualityEnabled;

    const handleChange = (event) => {
        setAggregationPeriod(event.target.value);
    };

    const handleSemestersChange = (event) => {
        const value = parseInt(event.target.value, 10);
        setSemesters(value);
        handleDateChange(subMonths(startOfDay(new Date()), 6 * value), startOfDay(new Date()));
    };

    const extractMetaDetails = (data) => {
        return Object.keys(data).reduce((acc, key) => {
            if (data[key].meta) {
                return {
                    ...acc,
                    [key]: data[key].meta,
                };
            }
            return acc;
        }, {});
    };

    React.useEffect(() => {
        if (executiveApi && executiveApi.status === "SUCCESS") {
            const metaDetails = extractMetaDetails(executiveApi.data);
            setMeta(metaDetails);
        }
    }, [executiveApi.status]);

    const renderStyledValue = () => {
        if (!aggregationPeriod) return null;
        let aggregationPeriodItem;
        if (aggregationPeriod === "day") aggregationPeriodItem = "Daily";
        if (aggregationPeriod === "week") aggregationPeriodItem = "Weekly";
        if (aggregationPeriod === "month") aggregationPeriodItem = "Monthly";
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    fontWeight: "300",
                }}
            >
                {aggregationPeriodItem}
            </Box>
        );
    };

    const renderStyledPeriodValue = () => {
        if (!semesters) return null;
        let semestersItems;
        if (semesters === 1) semestersItems = "6 Months";
        if (semesters === 2) semestersItems = "1 Year";
        if (semesters === 3) semestersItems = "18 Months";
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    fontWeight: "300",
                }}
            >
                {semestersItems}
            </Box>
        );
    };

    return (
        <>
            <Grid container spacing={2} pt={1} mb={1}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel id="agg-period-select-label" size="small">
                            Data aggregation period
                        </InputLabel>
                        <Select
                            labelId="agg-period-select-label"
                            id="agg-period-select"
                            value={aggregationPeriod}
                            label="Select data aggregation period"
                            size="small"
                            renderValue={renderStyledValue}
                            onChange={handleChange}
                        >
                            <StyledMenuItem value="day" disabled={selectedTeam === "all"}>
                                Daily
                            </StyledMenuItem>
                            <StyledMenuItem value="week">Weekly</StyledMenuItem>
                            <StyledMenuItem value="month">Monthly</StyledMenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel id="period-select-label" size="small">
                            Data period
                        </InputLabel>
                        <Select
                            labelId="period-select-label"
                            id="period-select"
                            value={semesters}
                            label="Select data period"
                            size="small"
                            renderValue={renderStyledPeriodValue}
                            onChange={handleSemestersChange}
                        >
                            <StyledMenuItem value="1">6 Months</StyledMenuItem>
                            <StyledMenuItem value="2">1 Year</StyledMenuItem>
                            <StyledMenuItem value="3">18 Months</StyledMenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <OverviewRefreshSection
                        cachedAt={executiveApi && executiveApi.data && executiveApi.data.cachedAt}
                        setRefresh={setRefresh}
                    />
                </Grid>
            </Grid>
            {(dashboard?.templates || []).map((template, templateIndex) => {
                return (
                    <Stack key={template.id} mb={4}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: "100%" }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <TemplateTitle
                                    text={template.title.value}
                                    templateIndex={templateIndex}
                                />
                                <KnowledgebaseIconLink
                                    name={template.knowledgeBaseName}
                                    markdownId={null}
                                />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <InvalidIntegrationButton
                                    dependencies={template.dependencies}
                                    isTeams={false}
                                />
                            </Stack>
                        </Stack>
                        <Template
                            viewType="exec-ts"
                            onEditMode={false}
                            toolsScrapping={[]}
                            template={template}
                            templateIndex={templateIndex}
                            defaultDateRange={dashboard.defaultDateRange}
                            setTemplateNoData={() => {}}
                            setEarliestFirstScrapped={() => {}}
                            setApiStatus={() => {}}
                        />
                    </Stack>
                );
            })}
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.investment}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.investment && (
                                <>
                                    <HeaderStack>
                                        <H>Investment</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        investment: !prevCharts.investment,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("investment")}
                                                disabled={
                                                    meta &&
                                                    meta.code &&
                                                    meta.code.accuracy &&
                                                    meta.code.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi && executiveTimeseriesApi.data
                                        }
                                    >
                                        <InvestmentMainChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data
                                            }
                                            accuracy={meta && meta.code && meta.code.accuracy}
                                            status={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.status
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.investment && (
                                <>
                                    <HeaderStack>
                                        <H>Investment</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        investment: !prevCharts.investment,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("investment")}
                                                disabled={
                                                    meta &&
                                                    meta.code &&
                                                    meta.code.accuracy &&
                                                    meta.code.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeDaysDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Code Days
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            The average user per time per day that was made
                                            available for him/her to work on core activities (time
                                            without breaks, out of office and collaboration).
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.collaborationDaysDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Collaboration Days
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Average user per day time invested in collaboration (ie
                                            chatting and in meetings). All collaboration events are
                                            given a time duration and are combined into unified
                                            durations if there is less than 15 minutes of distance
                                            between the end of one and the beginning of the other.
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.balanceDaysDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Balance Days
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Average user per day time invested in the team&apos;s
                                            healthy work-life balance (ie breaks and out of office).
                                            Similar event combination mechanism to the one for
                                            collaboration is used here as well.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.throughput}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.throughput && (
                                <>
                                    <HeaderStack>
                                        <H>Throughput</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        throughput: !prevCharts.throughput,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("throughput")}
                                                disabled={
                                                    meta &&
                                                    meta.throughput &&
                                                    meta.throughput.accuracy &&
                                                    meta.throughput.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.throughput
                                        }
                                    >
                                        <DeliveryThroughputChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.throughput
                                            }
                                            accuracy={
                                                meta && meta.throughput && meta.throughput.accuracy
                                            }
                                            status={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.status
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.throughput && (
                                <>
                                    <HeaderStack>
                                        <H>Throughput</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        throughput: !prevCharts.throughput,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("throughput")}
                                                disabled={
                                                    meta &&
                                                    meta.throughput &&
                                                    meta.throughput.accuracy &&
                                                    meta.throughput.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliveryThroughputTicketsDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Tickets Done
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Delivery output produced measured in issues competed (ie
                                            transitioned to a done state) . An indication of the
                                            amount of work delivered.
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliveryThroughputPRDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Pull Requests Merged
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Delivery output produced measured in Pull Requests
                                            merged. An indication of the amount of work delivered.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.leadTime}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.leadTime && (
                                <>
                                    <HeaderStack>
                                        <H>Lead time</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        leadTime: !prevCharts.leadTime,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("leadTime")}
                                                disabled={
                                                    meta &&
                                                    meta.leadTime &&
                                                    meta.leadTime.accuracy &&
                                                    meta.leadTime.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.leadTime
                                        }
                                    >
                                        <DeliveryLeadTimeChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.leadTime
                                            }
                                            accuracy={
                                                meta && meta.leadTime && meta.leadTime.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.leadTime && (
                                <>
                                    <HeaderStack>
                                        <H>Lead time</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        leadTime: !prevCharts.leadTime,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("leadTime")}
                                                disabled={
                                                    meta &&
                                                    meta.leadTime &&
                                                    meta.leadTime.accuracy &&
                                                    meta.leadTime.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliveryLeadTimeIssuesDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Issues Lead Time
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Unit of work completion rate, measured in issues lead
                                            time. An indication of rate of speed of the delivered
                                            work.
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliveryLeadTimePRDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Pull Requests Lead Time
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Unit of work completion rate, measured in Pull Requests
                                            lead time. An indication of rate of speed of the
                                            delivered work.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.scopeCreep}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.scopeCreep && (
                                <>
                                    <HeaderStack>
                                        <H>Sprint Planning (In)Accuracy</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        scopeCreep: !prevCharts.scopeCreep,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("scopeCreep")}
                                                disabled={
                                                    meta &&
                                                    meta.scopeCreep &&
                                                    meta.scopeCreep.accuracy &&
                                                    meta.scopeCreep.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.scopeCreep
                                        }
                                    >
                                        <DeliveryScopeCreepChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.scopeCreep
                                            }
                                            accuracy={
                                                meta && meta.scopeCreep && meta.scopeCreep.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.scopeCreep && (
                                <>
                                    <HeaderStack>
                                        <H>Sprint Planning (In)Accuracy</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        scopeCreep: !prevCharts.scopeCreep,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("scopeCreep")}
                                                disabled={
                                                    meta &&
                                                    meta.scopeCreep &&
                                                    meta.scopeCreep.accuracy &&
                                                    meta.scopeCreep.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliverySprintPlanningDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Sprint Planning Accuracy
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Also known as scope creep, these are the tickets that
                                            the team contributed in some way (created or had
                                            assigned on them) that were part of a sprint, but were
                                            including into it after its start date.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.reviewTime}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.reviewTime && (
                                <>
                                    <HeaderStack>
                                        <H>Review time</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        reviewTime: !prevCharts.reviewTime,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("reviewTime")}
                                                disabled={
                                                    meta &&
                                                    meta.reviewTime &&
                                                    meta.reviewTime.accuracy &&
                                                    meta.reviewTime.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.reviewTime
                                        }
                                    >
                                        <DeliveryReviewTimeChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.reviewTime
                                            }
                                            accuracy={
                                                meta && meta.reviewTime && meta.reviewTime.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.reviewTime && (
                                <>
                                    <HeaderStack>
                                        <H>Review time</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        reviewTime: !prevCharts.reviewTime,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("reviewTime")}
                                                disabled={
                                                    meta &&
                                                    meta.reviewTime &&
                                                    meta.reviewTime.accuracy &&
                                                    meta.reviewTime.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.deliveryReviewingEfficiencyDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Review Time
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            The time to review and merge a Pull Request.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={12}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.creditCost}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.creditCost && (
                                <>
                                    <HeaderStack>
                                        <H>Personal Credit Cost</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        creditCost: !prevCharts.creditCost,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("creditCost")}
                                                disabled={
                                                    meta &&
                                                    meta.longHours &&
                                                    meta.longHours.accuracy &&
                                                    meta.longHours.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi && executiveTimeseriesApi.data
                                        }
                                    >
                                        <CreditExpenditureMainChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data
                                            }
                                            status={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.status
                                            }
                                            accuracy={
                                                meta && meta.longHours && meta.longHours.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.creditCost && (
                                <>
                                    <HeaderStack>
                                        <H>Personal Credit Cost</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        creditCost: !prevCharts.creditCost,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("creditCost")}
                                                disabled={
                                                    meta &&
                                                    meta.longHours &&
                                                    meta.longHours.accuracy &&
                                                    meta.longHours.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.personalCreditLongHoursDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Long Working Hours
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            These are the average hours per user-day that are above
                                            the daily threshold of 7 hours of work in a day. This
                                            threshold does not depend on your set working hours.
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.personalCreditOutOfHoursDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Out of hours working
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Average user-day hours spent communicating during out of
                                            office hours. This depends on the users&apos; working
                                            hours set on their personal settings, or uses the
                                            default extended working hours.
                                        </InfoText>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.personalCreditFocusDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Available Focus Time
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            This is average amount of time per user per day
                                            available for deep focus. Which is defined as the time
                                            during which the user had no distractions for equal or
                                            more than 1h during his/her working hours. This time is
                                            important in order to allow the user get into a deep
                                            focus state to deliver efficiently and in high quality.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.backlogSize}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.backlogSize && (
                                <>
                                    <HeaderStack>
                                        <H>Backlog Size</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        backlogSize: !prevCharts.backlogSize,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("backlogSize")}
                                                disabled={
                                                    meta &&
                                                    meta.backlog &&
                                                    meta.backlog.accuracy &&
                                                    meta.backlog.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi && executiveTimeseriesApi.data
                                        }
                                    >
                                        <CreditExpenditureBacklogChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data
                                            }
                                            status={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.status
                                            }
                                            accuracy={meta && meta.backlog && meta.backlog.accuracy}
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.backlogSize && (
                                <>
                                    <HeaderStack>
                                        <H>Backlog Size</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        backlogSize: !prevCharts.backlogSize,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("backlogSize")}
                                                disabled={
                                                    meta &&
                                                    meta.backlog &&
                                                    meta.backlog.accuracy &&
                                                    meta.backlog.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeCreditBacklogDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Backlog Size
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Amount of current backlog tickets, and the new issues
                                            added in the backlog during the period.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.bugsDetected}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.bugsDetected && (
                                <>
                                    <HeaderStack>
                                        <H>Bugs Detected</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        bugsDetected: !prevCharts.bugsDetected,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("bugsDetected")}
                                                disabled={
                                                    meta &&
                                                    meta.bugsDetected &&
                                                    meta.bugsDetected.accuracy &&
                                                    meta.bugsDetected.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.bugsDetected
                                        }
                                    >
                                        <CreditExpenditureBugsDetectedChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.bugsDetected
                                            }
                                            accuracy={
                                                meta &&
                                                meta.bugsDetected &&
                                                meta.bugsDetected.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.bugsDetected && (
                                <>
                                    <HeaderStack>
                                        <H>Bugs Detected</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        bugsDetected: !prevCharts.bugsDetected,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("bugsDetected")}
                                                disabled={
                                                    meta &&
                                                    meta.bugsDetected &&
                                                    meta.bugsDetected.accuracy &&
                                                    meta.bugsDetected.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeCreditBugsDetectedDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Bugs Detected
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Amount of bug tickets assigned or created by this group
                                            of people during the period.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.rework}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.rework && (
                                <>
                                    <HeaderStack>
                                        <H>Reworked Code</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        rework: !prevCharts.rework,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("rework")}
                                                disabled={
                                                    meta &&
                                                    meta.rework &&
                                                    meta.rework.accuracy &&
                                                    meta.rework.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.rework
                                        }
                                    >
                                        <CreditExpenditureReworkChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.rework
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.rework && (
                                <>
                                    <HeaderStack>
                                        <H>Reworked Code</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        rework: !prevCharts.rework,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("rework")}
                                                disabled={
                                                    meta &&
                                                    meta.rework &&
                                                    meta.rework.accuracy &&
                                                    meta.rework.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeCreditReworkDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Reworked Code Ratio
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            Ratio of existing code changed over the total amount of
                                            changes done in the code. Indication of how much old
                                            code is getting changed vs new code added without
                                            changing the old. Should be kept in a healthy ratio
                                            around the middle zone, depending on the work done by
                                            the team (new project or BAU).
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.maintainability}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.maintainability && (
                                <>
                                    <HeaderStack>
                                        <H>Maintainability Index Changes</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        maintainability:
                                                            !prevCharts.maintainability,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("maintainability")}
                                                disabled={
                                                    meta &&
                                                    meta.maintainability &&
                                                    meta.maintainability.accuracy &&
                                                    meta.maintainability.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.maintainability
                                        }
                                    >
                                        <CreditExpenditureMaintainabilityChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.maintainability
                                            }
                                            codeQualityActivated={codeQualityActivated}
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.maintainability && (
                                <>
                                    <HeaderStack>
                                        <H>Maintainability Index Changes</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        maintainability:
                                                            !prevCharts.maintainability,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("maintainability")}
                                                disabled={
                                                    meta &&
                                                    meta.maintainability &&
                                                    meta.maintainability.accuracy &&
                                                    meta.maintainability.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeCreditMaintainabilityDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Maintainability Index
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            The sum of the changes on the code maintainability index
                                            of all repos altered by this group of people in this
                                            period.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ReactCardFlip
                        isFlipped={flippedCharts.codeSilos}
                        containerStyle={{ width: "100%", height: "100%" }}
                    >
                        <ChartPaper>
                            {!flippedCharts.codeSilos && (
                                <>
                                    <HeaderStack>
                                        <H>Code silos</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        codeSilos: !prevCharts.codeSilos,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("codeSilos")}
                                                disabled={
                                                    meta &&
                                                    meta.codeSilos &&
                                                    meta.codeSilos.accuracy &&
                                                    meta.codeSilos.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <ShowWithSkeleton
                                        fetchAction={executiveTimeseriesApi}
                                        height="100%"
                                        width="100%"
                                        streamingProperty={
                                            executiveTimeseriesApi &&
                                            executiveTimeseriesApi.data &&
                                            executiveTimeseriesApi.data.codeSilos
                                        }
                                    >
                                        <CreditExpenditureSilosChart
                                            data={
                                                executiveTimeseriesApi &&
                                                executiveTimeseriesApi.data &&
                                                executiveTimeseriesApi.data.codeSilos
                                            }
                                            accuracy={
                                                meta && meta.codeSilos && meta.codeSilos.accuracy
                                            }
                                        />
                                    </ShowWithSkeleton>
                                </>
                            )}
                        </ChartPaper>
                        <ChartPaper>
                            {flippedCharts.codeSilos && (
                                <>
                                    <HeaderStack>
                                        <H>Code silos</H>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setFlippedCharts((prevCharts) => ({
                                                        ...prevCharts,
                                                        codeSilos: !prevCharts.codeSilos,
                                                    }))
                                                }
                                                aria-label="View info on metrics"
                                                color="secondary"
                                            >
                                                <Tooltip title="Information">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedChart("codeSilos")}
                                                disabled={
                                                    meta &&
                                                    meta.codeSilos &&
                                                    meta.codeSilos.accuracy &&
                                                    meta.codeSilos.accuracy.level === "zero"
                                                }
                                                aria-label="expand chart"
                                                color="secondary"
                                                target="_blank"
                                            >
                                                <Tooltip title="Expand/Details">
                                                    <FitScreenIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </Stack>
                                    </HeaderStack>
                                    <InfoStack>
                                        <InfoHeaderText>
                                            <ExternalLink
                                                href={getDocsPath(
                                                    org,
                                                    docsPathConstants.codeCreditSilosDefinition
                                                )}
                                                underline="none"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Code Silos
                                            </ExternalLink>
                                        </InfoHeaderText>
                                        <InfoText>
                                            These are repos that have most (ie more than 2/3rds) of
                                            their contributions done by one single contributor from
                                            this group of people. This indigates potential knowledge
                                            silos within this group. The tooltip shows the repos in
                                            order of last committed to and last having the most
                                            commits.
                                        </InfoText>
                                    </InfoStack>
                                </>
                            )}
                        </ChartPaper>
                    </ReactCardFlip>
                </Grid>
            </Grid>
            <OverviewExpandModal
                expandedChart={expandedChart}
                setExpandedChart={setExpandedChart}
                data={executiveTimeseriesApi && executiveTimeseriesApi.data}
                status={executiveTimeseriesApi && executiveTimeseriesApi.status}
                codeQualityActivated={codeQualityActivated}
            />
        </>
    );
};

export default OverviewAnalytics;
